import React, {useCallback, useMemo, useState} from "react"
import {PieChart, Pie, Cell, Tooltip, ResponsiveContainer} from "recharts"
import {platformAPI} from "../../../api/axiosConfig"
import {useSirenResponses, ResponsesCycleStats} from "../hooks/useSirenAlertWS"
import {Card, PieTooltip, RESPONSE_COLORS, ResponseRecipientsModal, TemplateSearchSelect, MassWarningTemplate, CycleTabSelector} from "./AlertStatusShared"
import {useGetMassWarningTemplateOptions} from "../hooks/useGetMassWarningTemplates"
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID"
import {useSiren} from "../SirenContext"

function normaliseResponseValue(raw: string, options: string[]): string | null {
    const n = parseInt(raw, 10)
    if (!isNaN(n) && n >= 1 && n <= options.length) return options[n - 1]
    if (options.includes(raw)) return raw
    return null
}

export function ResponsesCard({
    alertID,
    responseOptions,
    selectedCycle,
    onCycleChange,
}: {
    alertID: string
    responseOptions: string[]
    selectedCycle: number | "all"
    onCycleChange: (c: number | "all") => void
}) {
    const {data} = useSirenResponses(alertID)

    // Aggregate for the selected cycle
    const aggregated = useMemo(() => {
        if (!data) return {total: 0, responded: 0, responseValues: {} as Record<string, number>}
        const cycles: ResponsesCycleStats[] = selectedCycle === "all"
            ? data.cycles
            : data.cycles.filter(c => c.cycle === selectedCycle)

        const responseValues: Record<string, number> = {}
        let responded = 0
        for (const cs of cycles) {
            responded += cs.responded
            for (const [k, v] of Object.entries(cs.responseValues)) {
                responseValues[k] = (responseValues[k] ?? 0) + v
            }
        }

        let total: number
        if (selectedCycle === "all") {
            total = data.total
        } else {
            const idx = selectedCycle - 1
            total = (data.cycleTotals?.[idx] != null) ? data.cycleTotals[idx] : data.total
        }

        return {total, responded, responseValues}
    }, [data, selectedCycle])

    const allCycles = useMemo(() => {
        if (!data) return []
        return [...new Set(data.cycles.map(c => c.cycle))].sort((a, b) => a - b)
    }, [data])

    const responsePieData = useMemo(() => {
        const options = responseOptions ?? []
        const optionCounts: Record<string, number> = {}
        let invalidCount = 0

        for (const [raw, count] of Object.entries(aggregated.responseValues)) {
            const label = normaliseResponseValue(raw, options)
            if (label) {
                optionCounts[label] = (optionCounts[label] ?? 0) + count
            } else {
                invalidCount += count
            }
        }

        const pieData: {name: string; rawValue: string; value: number; color: string}[] = options.map((opt, i) => ({
            name: opt,
            rawValue: String(i + 1),
            value: optionCounts[opt] ?? 0,
            color: RESPONSE_COLORS[i + 1] ?? RESPONSE_COLORS[RESPONSE_COLORS.length - 1],
        }))

        if (invalidCount > 0) {
            pieData.push({name: "Invalid", rawValue: "__invalid__", value: invalidCount, color: RESPONSE_COLORS[RESPONSE_COLORS.length - 1]})
        }

        pieData.push({
            name: "No Response",
            rawValue: "",
            value: Math.max(0, aggregated.total - aggregated.responded),
            color: RESPONSE_COLORS[0],
        })

        return pieData
    }, [responseOptions, aggregated])

    const [responseModal, setResponseModal] = useState<{label: string; rawValue: string} | null>(null)

    const {clientID} = useSiren()
    const {data: templateData, setSearch: setTemplateSearch} = useGetMassWarningTemplateOptions(clientID ?? null)
    const templates = templateData?.results ?? []
    const [followUpSelected, setFollowUpSelected] = useState<Set<string>>(new Set())
    const [followUpTemplate, setFollowUpTemplate] = useState<MassWarningTemplate | null>(null)
    const [followUpLoading, setFollowUpLoading] = useState(false)
    const {navigateWithClientIDPath} = useNavigateWithClientID()

    const toggleFollowUp = useCallback((rawValue: string) => {
        setFollowUpSelected(prev => {
            const next = new Set(prev)
            next.has(rawValue) ? next.delete(rawValue) : next.add(rawValue)
            return next
        })
    }, [])

    const sendFollowUp = useCallback(async () => {
        if (followUpSelected.size === 0) return
        setFollowUpLoading(true)
        try {
            const allContactIDs: string[] = []
            for (const rawValue of followUpSelected) {
                const res = await platformAPI.get<{contactIDs: string[]}>(`api/siren/alerts/${alertID}/recipients/contact-ids`, {
                    params: {responseValue: rawValue},
                })
                allContactIDs.push(...res.data.contactIDs)
            }
            const unique = [...new Set(allContactIDs)]
            if (unique.length === 0) return
            const key = crypto.randomUUID()
            localStorage.setItem(`mass-warning-contacts-${key}`, JSON.stringify(unique))
            const templateID = followUpTemplate ? followUpTemplate.id : "no_template"
            window.open(navigateWithClientIDPath(`siren/alerts?subPage=create&contactList=${key}&templateID=${templateID}`), "_blank")
        } finally {
            setFollowUpLoading(false)
        }
    }, [alertID, followUpSelected, followUpTemplate, navigateWithClientIDPath])

    return (
        <>
            <Card
                title="Responses"
                headerActions={
                    <CycleTabSelector cycles={allCycles} selected={selectedCycle} onChange={onCycleChange} />
                }
            >
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            {label: "Total", value: aggregated.total, color: "text-dark-dark-grey"},
                            {label: "Responded", value: aggregated.responded, color: "text-green"},
                            {label: "Pending", value: aggregated.total - aggregated.responded, color: "text-dark-orange"},
                        ].map(({label, value, color}) => (
                            <div key={label} className="flex flex-col items-center gap-1 bg-ice rounded-lg p-3">
                                <span className={`text-2xl font-bold ${color}`}>{value}</span>
                                <span className="text-xs text-grey">{label}</span>
                            </div>
                        ))}
                    </div>

                    {aggregated.total > 0 && (
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between text-xs text-grey">
                                <span>Response rate</span>
                                <span className="font-semibold text-dark-dark-grey">
                                    {Math.round((aggregated.responded / aggregated.total) * 100)}%
                                </span>
                            </div>
                            <div className="bg-light-grey rounded-full h-3 overflow-hidden">
                                <div
                                    className="h-3 bg-green rounded-full transition-all duration-500"
                                    style={{width: `${(aggregated.responded / aggregated.total) * 100}%`}}
                                />
                            </div>
                        </div>
                    )}

                    {responseOptions.length > 0 && (
                        <>
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                    <Pie
                                        data={responsePieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius="55%"
                                        outerRadius="80%"
                                        dataKey="value"
                                        nameKey="name"
                                        strokeWidth={0}
                                        isAnimationActive={false}
                                    >
                                        {responsePieData.map((entry, i) => {
                                            const clickable = entry.value > 0 && entry.rawValue !== "__invalid__"
                                            return (
                                                <Cell
                                                    key={i}
                                                    fill={entry.color}
                                                    cursor={clickable ? "pointer" : "default"}
                                                    onClick={() => clickable && setResponseModal({label: entry.name, rawValue: entry.rawValue})}
                                                    className={clickable ? "hover:opacity-80 transition-opacity" : ""}
                                                />
                                            )
                                        })}
                                    </Pie>
                                    <Tooltip content={<PieTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>

                            <div className="flex flex-col gap-2">
                                {responsePieData.map((entry, i) => {
                                    const pct = aggregated.total > 0 ? (entry.value / aggregated.total) * 100 : 0
                                    const checked = followUpSelected.has(entry.rawValue)
                                    const clickable = entry.value > 0 && entry.rawValue !== "__invalid__"
                                    return (
                                        <div key={i} className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2 text-xs">
                                                <button
                                                    className={`font-medium flex-1 text-left ${clickable ? "hover:opacity-80 transition-opacity cursor-pointer" : "cursor-default"}`}
                                                    style={{color: entry.color}}
                                                    onClick={() => clickable && setResponseModal({label: entry.name, rawValue: entry.rawValue})}
                                                >
                                                    {entry.name}
                                                </button>
                                                <span className="text-grey">{entry.value} ({pct.toFixed(0)}%)</span>
                                                <input
                                                    type="checkbox"
                                                    className="w-3.5 h-3.5 accent-blue cursor-pointer"
                                                    checked={checked}
                                                    onChange={() => toggleFollowUp(entry.rawValue)}
                                                />
                                            </div>
                                            <div
                                                className={`bg-light-grey rounded-full h-3 overflow-hidden ${clickable ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                                                onClick={() => clickable && setResponseModal({label: entry.name, rawValue: entry.rawValue})}
                                            >
                                                <div
                                                    className="h-3 rounded-full transition-all duration-500"
                                                    style={{width: `${pct}%`, background: entry.color}}
                                                />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            {followUpSelected.size > 0 && (
                                <div className="flex items-center gap-2 pt-3 mt-1 border-t border-light-grey">
                                    <TemplateSearchSelect
                                        templates={templates}
                                        value={followUpTemplate}
                                        onSearch={setTemplateSearch}
                                        onChange={setFollowUpTemplate}
                                    />
                                    <button
                                        onClick={sendFollowUp}
                                        disabled={followUpLoading}
                                        className="px-3 py-1.5 rounded border border-light-grey text-xs font-medium text-grey hover:bg-ice transition-colors disabled:opacity-50 whitespace-nowrap"
                                    >
                                        {followUpLoading ? "Loading…" : "Send Follow-up"}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Card>

            {responseModal && (
                <ResponseRecipientsModal
                    alertID={alertID}
                    label={responseModal.label}
                    rawValue={responseModal.rawValue}
                    onClose={() => setResponseModal(null)}
                />
            )}
        </>
    )
}
